import { Component, Input, OnInit, HostBinding, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NbDialogRef, NbCardModule, NbListModule, NbCheckboxModule, NbButtonModule, NbSelectModule, NbOptionModule, NbSpinnerModule } from '@nebular/theme';
import { Category } from '../../../../model/category.model';
import { CategoryService } from 'src/app/deebr/service/category.service';
import { MetaCategory } from '../../../../model/meta-category.model';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-category-selection-modal',
    templateUrl: './category-selection-modal.component.html',
    styleUrls: ['./category-selection-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        NbCardModule,
        NbListModule,
        NbCheckboxModule,
        NbButtonModule,
        NbSelectModule,
        NbOptionModule,
        NbSpinnerModule,
        FormsModule
    ]
})
export class CategorySelectionModalComponent implements OnInit {

    @Input() categories: Category[] = []; // Initial list or ignored if meta is loaded
    @Input() selectedCategoryIds: number[] = [];
    @Input() initialMetaCategoryId: number | null = null;

    @HostBinding('class.loaded') isLoaded = false;

    tempSelectedIds: Set<number> = new Set();
    metaCategories: MetaCategory[] = [];
    selectedMetaId: number | null = null;
    displayedCategories: Category[] = [];
    filteredCategories: Category[] = [];
    loading = false;
    searchTerm: string = '';
    filtering = false;
    private filterTimer: any = null;
    private hideSpinnerTimer: any = null;

    onSearchChange(value: string) {
        this.searchTerm = value;
        // Cancel toute hide en cours pour éviter qu'elle masque le spinner pendant un nouveau cycle
        if (this.hideSpinnerTimer) { clearTimeout(this.hideSpinnerTimer); this.hideSpinnerTimer = null; }
        if (this.filterTimer) clearTimeout(this.filterTimer);

        this.filtering = true;
        this.cdr.detectChanges(); // force le paint immédiat du spinner avant le filter potentiellement coûteux

        this.filterTimer = setTimeout(() => {
            this.recomputeFilter();
            // Garantit une visibilité minimale du spinner, même si le filtre est instantané
            this.hideSpinnerTimer = setTimeout(() => {
                this.filtering = false;
                this.hideSpinnerTimer = null;
            }, 250);
        }, 150);
    }

    private recomputeFilter() {
        const q = this.searchTerm.trim().toLowerCase();
        this.filteredCategories = q
            ? this.displayedCategories.filter(c => (c.label || '').toLowerCase().includes(q))
            : this.displayedCategories;
    }

    trackById(_index: number, cat: Category): number {
        return cat.id;
    }

    constructor(
        protected ref: NbDialogRef<CategorySelectionModalComponent>,
        private categoryService: CategoryService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.tempSelectedIds = new Set(this.selectedCategoryIds);
        this.selectedMetaId = this.initialMetaCategoryId;

        this.categoryService.loadCategories().subscribe(() => {
            this.metaCategories = this.categoryService.metaCategories;

            // If no meta selected, pick first or from input
            if (!this.selectedMetaId && this.metaCategories.length > 0) {
                // Try to infer from existing input categories if possible, otherwise default
                if (this.categories.length > 0) {
                    this.selectedMetaId = this.categories[0].meta_category;
                } else {
                    this.selectedMetaId = this.metaCategories[0].id;
                }
            }

            this.updateDisplayedCategories();
            this.isLoaded = true;
        });
    }

    onMetaChange(metaId: number) {
        this.selectedMetaId = metaId;
        this.tempSelectedIds.clear();
        this.searchTerm = '';
        this.loading = true;
        this.displayedCategories = [];
        this.cdr.detectChanges();

        setTimeout(() => {
            this.categoryService.getCategories([this.selectedMetaId!]).subscribe(cats => {
                this.displayedCategories = cats;
                this.recomputeFilter();
                this.loading = false;
            });
        });
    }

    updateDisplayedCategories() {
        if (this.selectedMetaId) {
            this.categoryService.getCategories([this.selectedMetaId]).subscribe(cats => {
                this.displayedCategories = cats;
                this.recomputeFilter();
            });
        } else {
            this.displayedCategories = [];
            this.recomputeFilter();
        }
    }

    toggleSelection(catId: number, checked: boolean) {
        if (checked) {
            this.tempSelectedIds.add(catId);
        } else {
            this.tempSelectedIds.delete(catId);
        }
    }

    submit() {
        this.ref.close({
            metaCategoryId: this.selectedMetaId,
            categoryIds: Array.from(this.tempSelectedIds)
        });
    }

    cancel() {
        this.ref.close();
    }
}
